import React from "react";
import { withFeatureSafe } from 'Common/ErrorBoundaries';
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { PasswordValidator } from "Utils/validators";
import { getPasswordStrength } from "Utils/passwordStrength";
import { strengthLabel, strengthColor, strengthTextColor } from "Utils/Utils";
import { 
  PASSWORD_REQUIREMENTS, 
  PASSWORD_INDICATOR_ANIMATION,
  PASSWORD_REQUIREMENTS_LIST_ANIMATION,
  PASSWORD_REQUIREMENT_ITEM_ANIMATION,
  PASSWORD_PROGRESS_BAR_ANIMATION
} from "Common/Constants";
import { IoCloseCircleOutline } from "react-icons/io5";

const PasswordStrengthIndicator = ({ password }) => {
    const strength = getPasswordStrength(password);

    const passwordValidation = PasswordValidator(password);

    return (
        <div className="mt-1">
            <AnimatePresence>
                {password && (
                    <motion.div
                        key="indicator"
                        {...PASSWORD_INDICATOR_ANIMATION}
                    >
                        <p className="flex justify-between my-1 text-sm text-gray-600">
                            <span>Password strength</span>
                            <span className={strengthTextColor(strength)}>{strengthLabel(strength)}</span>
                        </p>
                        <div className="overflow-hidden w-full h-2 bg-gray-200 rounded-full">
                            <motion.div
                                className="h-2 rounded-full"
                                animate={{ 
                                    width: `${(strength / 4) * 100}%`,
                                    backgroundColor: strengthColor(strength)
                                }}
                                {...PASSWORD_PROGRESS_BAR_ANIMATION}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {password && passwordValidation !== null && (
                    <motion.ul
                        className="mt-2 text-sm text-gray-500"
                        {...PASSWORD_REQUIREMENTS_LIST_ANIMATION}
                    >
                        {PASSWORD_REQUIREMENTS.map((req, idx) =>
                            !req.test(password) ? (
                                <motion.li
                                    key={idx}
                                    className="flex gap-2 items-center"
                                    {...PASSWORD_REQUIREMENT_ITEM_ANIMATION}
                                    transition={{ 
                                        ...PASSWORD_REQUIREMENT_ITEM_ANIMATION.transition, 
                                        delay: idx * 0.05 
                                    }}
                                >
                                    <IoCloseCircleOutline className="w-4 h-4 text-red-400" />
                                    {req.label}
                                </motion.li>
                            ) : null
                        )}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
};

PasswordStrengthIndicator.propTypes = {
    password: PropTypes.string.isRequired,
};

export default withFeatureSafe(PasswordStrengthIndicator);